import React from "react";
import { motion } from "framer-motion";
import { Heart, ShieldCheck, Sparkles, Users, CalendarCheck, MessageCircle, ArrowRight, Star } from "lucide-react";

const serif = { fontFamily: "'Playfair Display', serif" };
const sans = { fontFamily: "'Manrope', sans-serif" };
const mono = { fontFamily: "'JetBrains Mono', monospace" };

const NAVY = "#0A2A5A";
const GOLD = "#F9AB0D";
const CREAM = "#F9F6F2";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function RedesignPreviewLanding() {
  return (
    <div style={{ ...sans, backgroundColor: CREAM }} className="min-h-screen text-slate-700">
      {/* Sticky glass nav */}
      <header
        className="sticky top-0 z-50 backdrop-blur-xl border-b"
        style={{ backgroundColor: "rgba(249,246,242,0.8)", borderColor: "rgba(10,42,90,0.1)" }}
        data-testid="preview-nav"
      >
        <div className="max-w-7xl mx-auto px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/images/logo-icon.png" alt="RecoveryMatch" className="w-10 h-10 rounded-xl" data-testid="preview-logo-icon" />
            <div className="text-2xl font-bold" style={serif}>
              <span style={{ color: NAVY }}>Recovery</span>
              <span style={{ color: GOLD }}>Match</span>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-10 text-sm font-semibold" style={{ color: NAVY }}>
            <span className="cursor-pointer hover:opacity-70 transition-opacity">How it Works</span>
            <span className="cursor-pointer hover:opacity-70 transition-opacity">Safety</span>
            <span className="cursor-pointer hover:opacity-70 transition-opacity">Stories</span>
            <span className="cursor-pointer hover:opacity-70 transition-opacity">Recovery+</span>
          </nav>
          <div className="flex items-center gap-4">
            <button className="text-sm font-bold hover:opacity-70 transition-opacity" style={{ color: NAVY }} data-testid="preview-nav-login">
              Log In
            </button>
            <button
              className="rounded-full px-6 py-2.5 text-sm font-bold transition-transform hover:scale-[1.03] active:scale-[0.98] shadow-sm"
              style={{ backgroundColor: GOLD, color: NAVY }}
              data-testid="preview-nav-join"
            >
              Join Free
            </button>
          </div>
        </div>
      </header>

      {/* HERO — Navy, editorial, photo breaking the container */}
      <section className="relative overflow-hidden" style={{ backgroundColor: NAVY }}>
        <div
          className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
          style={{ backgroundColor: GOLD }}
        />
        <div
          className="absolute bottom-0 -left-40 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl"
          style={{ backgroundColor: "#4A7BD0" }}
        />
        <div className="max-w-7xl mx-auto px-8 pt-24 pb-0 grid md:grid-cols-12 gap-12 items-end relative">
          <div className="md:col-span-6 pb-24">
            <motion.p
              {...fadeUp}
              className="text-xs uppercase tracking-[0.25em] font-bold mb-6"
              style={{ ...mono, color: GOLD }}
            >
              The dating app for people in recovery
            </motion.p>
            <motion.h1
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="text-5xl lg:text-7xl text-white leading-[1.05] tracking-tight mb-8"
              style={serif}
              data-testid="preview-hero-title"
            >
              Love, on the{" "}
              <span className="italic" style={{ color: GOLD }}>
                clearest
              </span>{" "}
              day of your life.
            </motion.h1>
            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.2 }}
              className="text-lg leading-relaxed mb-10 max-w-md"
              style={{ color: "rgba(255,255,255,0.75)" }}
            >
              RecoveryMatch connects clean and sober singles who understand the journey. Real people. Real clean
              time. Real connection — without the pressure of explaining yourself.
            </motion.p>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }} className="flex flex-wrap items-center gap-4">
              <button
                className="rounded-full px-8 py-4 font-bold text-base transition-transform hover:scale-[1.03] active:scale-[0.98] shadow-lg flex items-center gap-2"
                style={{ backgroundColor: GOLD, color: NAVY }}
                data-testid="preview-hero-cta"
              >
                Start Your Story <ArrowRight size={18} />
              </button>
              <button
                className="rounded-full px-8 py-4 font-semibold text-base border-2 text-white transition-colors hover:bg-white/10"
                style={{ borderColor: "rgba(255,255,255,0.3)" }}
                data-testid="preview-hero-app-cta"
              >
                Get the App
              </button>
            </motion.div>
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.4 }}
              className="mt-12 flex items-center gap-3"
            >
              <div className="flex -space-x-3">
                {[11, 32, 45, 59].map((n) => (
                  <img
                    key={n}
                    src={`https://i.pravatar.cc/80?img=${n}`}
                    alt=""
                    className="w-10 h-10 rounded-full border-2"
                    style={{ borderColor: NAVY }}
                  />
                ))}
              </div>
              <p className="text-sm" style={{ color: "rgba(255,255,255,0.65)" }}>
                <span className="font-bold text-white">2,400+ members</span> building clean and sober love stories
              </p>
            </motion.div>
          </div>

          <div className="md:col-span-6 relative">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="relative"
            >
              <img
                src="https://images.pexels.com/photos/16341254/pexels-photo-16341254.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Joyful couple"
                className="rounded-t-[2.5rem] w-full object-cover h-[520px] shadow-2xl"
                data-testid="preview-hero-image"
              />
              {/* Floating clean-time card */}
              <div
                className="absolute -left-8 top-16 bg-white rounded-2xl px-5 py-4 shadow-xl flex items-center gap-3"
                style={{ border: "1px solid rgba(10,42,90,0.08)" }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(249,171,13,0.15)" }}>
                  <CalendarCheck size={20} style={{ color: GOLD }} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold" style={{ ...mono, color: GOLD }}>
                    Clean Time
                  </p>
                  <p className="font-extrabold text-sm" style={{ color: NAVY }}>
                    2 years, 4 months
                  </p>
                </div>
              </div>
              {/* Floating match card */}
              <div
                className="absolute -right-4 bottom-16 bg-white rounded-2xl px-5 py-4 shadow-xl flex items-center gap-3"
                style={{ border: "1px solid rgba(10,42,90,0.08)" }}
              >
                <Heart size={20} fill={GOLD} style={{ color: GOLD }} />
                <p className="font-bold text-sm" style={{ color: NAVY }}>
                  It's a Match — 94% compatible
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BENTO features grid */}
      <section className="max-w-7xl mx-auto px-8 py-28">
        <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ ...mono, color: GOLD }}>
          Why RecoveryMatch
        </motion.p>
        <motion.h2 {...fadeUp} className="text-4xl lg:text-5xl tracking-tight mb-16 max-w-2xl" style={{ ...serif, color: NAVY }}>
          Built for the way recovery actually works.
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Large card */}
          <motion.div
            {...fadeUp}
            className="md:col-span-7 rounded-3xl p-10 relative overflow-hidden group transition-transform hover:-translate-y-1"
            style={{ backgroundColor: NAVY }}
            data-testid="preview-feature-matching"
          >
            <Sparkles size={28} style={{ color: GOLD }} className="mb-6" />
            <h3 className="text-2xl text-white mb-3" style={serif}>
              Curated daily matches, not endless swiping
            </h3>
            <p className="max-w-md leading-relaxed" style={{ color: "rgba(255,255,255,0.7)" }}>
              Our compatibility engine weighs your program, clean time, values and interests — then hand-delivers a
              few great matches a day. Quality over quantity, always.
            </p>
            <div className="mt-8 flex gap-3">
              {["AA", "NA", "SMART", "Celebrate"].map((p) => (
                <span
                  key={p}
                  className="rounded-full px-4 py-1.5 text-xs font-bold"
                  style={{ backgroundColor: "rgba(249,171,13,0.15)", color: GOLD }}
                >
                  {p}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Tall narrow card */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="md:col-span-5 bg-white rounded-3xl p-10 transition-all hover:-translate-y-1 hover:shadow-md"
            style={{ border: "1px solid rgba(10,42,90,0.06)" }}
            data-testid="preview-feature-safety"
          >
            <ShieldCheck size={28} style={{ color: NAVY }} className="mb-6" />
            <h3 className="text-2xl mb-3" style={{ ...serif, color: NAVY }}>
              Safety isn't a feature. It's the foundation.
            </h3>
            <p className="leading-relaxed text-slate-600">
              Photo verification, date check-ins with trusted contacts, one-tap SOS, and a community that reports —
              because your recovery comes first.
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            className="md:col-span-4 bg-white rounded-3xl p-10 transition-all hover:-translate-y-1 hover:shadow-md"
            style={{ border: "1px solid rgba(10,42,90,0.06)" }}
            data-testid="preview-feature-guided"
          >
            <MessageCircle size={28} style={{ color: GOLD }} className="mb-6" />
            <h3 className="text-xl mb-3 font-semibold" style={{ color: NAVY }}>
              Guided conversations
            </h3>
            <p className="text-sm leading-relaxed text-slate-600">
              Never stare at a blank message box. Thoughtful prompts help you go deeper than "hey."
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="md:col-span-4 bg-white rounded-3xl p-10 transition-all hover:-translate-y-1 hover:shadow-md"
            style={{ border: "1px solid rgba(10,42,90,0.06)" }}
            data-testid="preview-feature-cleantime"
          >
            <CalendarCheck size={28} style={{ color: GOLD }} className="mb-6" />
            <h3 className="text-xl mb-3 font-semibold" style={{ color: NAVY }}>
              Clean time, celebrated
            </h3>
            <p className="text-sm leading-relaxed text-slate-600">
              Milestones on your profile, anniversaries celebrated by the community. Your journey is the headline.
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="md:col-span-4 rounded-3xl p-10 transition-transform hover:-translate-y-1 relative overflow-hidden"
            style={{ backgroundColor: GOLD }}
            data-testid="preview-feature-community"
          >
            <Users size={28} style={{ color: NAVY }} className="mb-6" />
            <h3 className="text-xl mb-3 font-extrabold" style={{ color: NAVY }}>
              More than dating
            </h3>
            <p className="text-sm leading-relaxed font-medium" style={{ color: "rgba(10,42,90,0.8)" }}>
              A community feed, clean and sober events, and friendships that hold you accountable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonial band */}
      <section style={{ backgroundColor: NAVY }} className="py-24">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <div className="flex justify-center gap-1 mb-8">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={20} fill={GOLD} style={{ color: GOLD }} />
            ))}
          </div>
          <p className="text-3xl lg:text-4xl text-white leading-snug italic mb-8" style={serif}>
            "For the first time, I didn't have to explain why I don't drink. She already knew. We celebrated my
            3-year anniversary together last month."
          </p>
          <p className="text-sm font-bold uppercase tracking-widest" style={{ ...mono, color: GOLD }}>
            Marcus & Elena — Matched March 2025
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-8 py-28 text-center">
        <h2 className="text-4xl lg:text-6xl tracking-tight mb-6" style={{ ...serif, color: NAVY }}>
          Your next chapter starts clean and sober.
        </h2>
        <p className="text-lg text-slate-600 mb-10 max-w-xl mx-auto">
          Join free today. Meet people who measure life the same way you do — one day at a time.
        </p>
        <button
          className="rounded-full px-10 py-5 font-bold text-lg transition-transform hover:scale-[1.03] active:scale-[0.98] shadow-lg"
          style={{ backgroundColor: GOLD, color: NAVY }}
          data-testid="preview-final-cta"
        >
          Create Your Free Profile
        </button>
        <p className="mt-6 text-xs uppercase tracking-widest font-bold" style={{ ...mono, color: "rgba(10,42,90,0.4)" }}>
          Free forever plan • No credit card • Cancel anytime
        </p>
      </section>

      <footer style={{ backgroundColor: NAVY }} className="py-10 text-center">
        <p className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>
          © 2026 RecoveryMatch.com, LLC — Design Preview
        </p>
      </footer>
    </div>
  );
}
